import React from 'react';
import { FileText, MessageCircle, Trash2, X } from 'lucide-react';
import { Product, ProductComment } from '../../types';
import {
  createAdminProductComment,
  deleteAdminProductComment,
  getAdminProductComments,
  updateAdminProductComment,
} from '../../api/product-comments';

interface ProductCommentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: Product;
}

export const ProductCommentsModal: React.FC<ProductCommentsModalProps> = ({
  isOpen,
  onClose,
  product,
}) => {
  const [comments, setComments] = React.useState<ProductComment[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [loadError, setLoadError] = React.useState('');
  const [authorName, setAuthorName] = React.useState('판매자');
  const [content, setContent] = React.useState('');
  const [replyTarget, setReplyTarget] = React.useState<ProductComment | null>(null);
  const [editingComment, setEditingComment] = React.useState<ProductComment | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const loadComments = React.useCallback(async () => {
    if (!product.id) {
      return;
    }

    try {
      setIsLoading(true);
      setLoadError('');
      setComments(await getAdminProductComments(product.id));
    } catch (error) {
      console.error('Failed to load product comments', error);
      setComments([]);
      setLoadError('댓글을 불러오지 못했습니다.');
    } finally {
      setIsLoading(false);
    }
  }, [product.id]);

  React.useEffect(() => {
    if (!isOpen) {
      return;
    }

    setAuthorName('판매자');
    setContent('');
    setReplyTarget(null);
    setEditingComment(null);
    void loadComments();
  }, [isOpen, loadComments]);

  const handleCreateComment = async () => {
    const nextAuthorName = authorName.trim();
    const nextContent = content.trim();

    if (!nextAuthorName || !nextContent) {
      alert('작성자와 댓글 내용을 입력해주세요.');
      return;
    }

    try {
      setIsSubmitting(true);
      if (editingComment) {
        const updatedComment = await updateAdminProductComment(editingComment.id, {
          authorName: nextAuthorName,
          content: nextContent,
        });
        setComments((current) =>
          current.map((comment) =>
            comment.id === updatedComment.id ? updatedComment : comment,
          ),
        );
        setContent('');
        setAuthorName('판매자');
        setReplyTarget(null);
        setEditingComment(null);
        return;
      }

      const createdComment = await createAdminProductComment(product.id, {
        authorName: nextAuthorName,
        content: nextContent,
        parentId: replyTarget?.id,
      });
      setComments((current) => [createdComment, ...current]);
      setContent('');
      setReplyTarget(null);
      setEditingComment(null);
    } catch (error) {
      console.error('Failed to create admin product comment', error);
      alert('댓글 등록에 실패했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!window.confirm('이 댓글을 삭제하시겠습니까?')) {
      return;
    }

    try {
      await deleteAdminProductComment(commentId);
      setComments((current) =>
        current.filter(
          (comment) => comment.id !== commentId && comment.parentId !== commentId,
        ),
      );
    } catch (error) {
      console.error('Failed to delete product comment', error);
      alert('댓글 삭제에 실패했습니다.');
    }
  };

  if (!isOpen) return null;

  const rootComments = comments.filter((comment) => !comment.parentId);
  const repliesByParentId = comments
    .filter((comment) => comment.parentId)
    .reduce<Record<string, ProductComment[]>>((acc, comment) => {
      const parentId = comment.parentId;
      if (!parentId) return acc;
      acc[parentId] = [...(acc[parentId] || []), comment].sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      );
      return acc;
    }, {});

  return (
    <div className="fixed inset-0 z-[7000] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-md"
        onClick={onClose}
      />
      <div className="relative w-full max-w-2xl bg-white rounded-[32px] flex flex-col max-h-[90vh] shadow-2xl animate-in zoom-in duration-200">
        <div className="p-6 border-b flex justify-between items-center shrink-0">
          <div>
            <div className="flex items-center gap-2">
              <MessageCircle size={20} className="text-emerald-500" />
              <h4 className="text-xl font-black text-gray-900">
                [{product.name}] 댓글 관리
              </h4>
            </div>
            <p className="text-sm font-bold mt-1 text-emerald-600">
              댓글 {comments.length.toLocaleString()}개
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 bg-gray-100 rounded-full text-gray-400 hover:bg-gray-200 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4 no-scrollbar">
          <div className="rounded-2xl border border-gray-100 bg-gray-50/70 p-4 space-y-3">
            {(replyTarget || editingComment) && (
              <div className="rounded-xl border border-emerald-100 bg-white px-4 py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[10px] font-black uppercase text-emerald-500">
                      {editingComment ? '수정 대상' : '답댓글 대상'}
                    </p>
                    <p className="mt-1 truncate text-xs font-black text-gray-900">
                      {(editingComment || replyTarget)?.authorName}
                    </p>
                    <p className="mt-1 line-clamp-2 text-xs font-bold leading-relaxed text-gray-500">
                      {(editingComment || replyTarget)?.content}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setReplyTarget(null);
                      setEditingComment(null);
                      setAuthorName('판매자');
                      setContent('');
                    }}
                    className="shrink-0 text-[11px] font-black text-gray-400 hover:text-gray-700"
                  >
                    취소
                  </button>
                </div>
              </div>
            )}
            <div className="flex flex-col gap-2 sm:flex-row">
              <input
                className="h-11 w-full sm:w-32 shrink-0 rounded-xl border border-gray-200 bg-white px-3 text-xs font-bold outline-none focus:border-emerald-500"
                placeholder="작성자"
                maxLength={40}
                value={authorName}
                onChange={(e) => setAuthorName(e.target.value)}
              />
              <textarea
                className="min-h-[88px] flex-1 resize-none rounded-xl border border-gray-200 bg-white p-3 text-xs font-bold outline-none focus:border-emerald-500"
                placeholder={editingComment ? '수정할 댓글을 입력하세요.' : replyTarget ? '관리자 답댓글을 입력하세요.' : '관리자 댓글을 입력하세요.'}
                maxLength={500}
                value={content}
                onChange={(e) => setContent(e.target.value)}
              />
            </div>
            <button
              type="button"
              onClick={handleCreateComment}
              disabled={isSubmitting}
              className="h-11 w-full rounded-xl bg-gray-900 text-xs font-black text-white transition-all active:scale-[0.98] disabled:bg-gray-300"
            >
              {isSubmitting ? '저장 중' : editingComment ? '관리자 댓글 수정' : replyTarget ? '관리자 답댓글 등록' : '관리자 댓글 등록'}
            </button>
          </div>

          {isLoading ? (
            <div className="py-20 flex flex-col items-center justify-center text-gray-400 gap-2">
              <FileText size={48} strokeWidth={1} />
              <p className="text-sm font-bold">댓글을 불러오는 중입니다.</p>
            </div>
          ) : loadError ? (
            <div className="py-20 flex flex-col items-center justify-center text-gray-400 gap-2">
              <FileText size={48} strokeWidth={1} />
              <p className="text-sm font-bold">{loadError}</p>
            </div>
          ) : rootComments.length === 0 ? (
            <div className="py-20 flex flex-col items-center justify-center text-gray-400 gap-2">
              <FileText size={48} strokeWidth={1} />
              <p className="text-sm font-bold">댓글이 없습니다.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {rootComments.map((comment) => (
                <div
                  key={comment.id}
                  className="bg-gray-50/50 rounded-2xl p-4 border border-gray-100 hover:bg-white hover:shadow-sm transition-all"
                >
                  <div className="flex justify-between items-start gap-4">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-black text-gray-900">
                        {comment.authorName}
                      </p>
                      <p className="mt-1 text-[11px] font-bold text-gray-400">
                        {new Date(comment.createdAt).toLocaleString('ko-KR')}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleDeleteComment(comment.id)}
                      className="p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                      title="댓글 삭제"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                  <p className="mt-3 whitespace-pre-wrap break-words text-sm font-bold leading-relaxed text-gray-600">
                    {comment.content}
                  </p>
                  <div className="mt-3 flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setReplyTarget(comment);
                        setEditingComment(null);
                        setAuthorName('판매자');
                        setContent('');
                      }}
                      className="text-[11px] font-black text-emerald-600"
                    >
                      답댓글
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setEditingComment(comment);
                        setReplyTarget(null);
                        setAuthorName(comment.authorName);
                        setContent(comment.content);
                      }}
                      className="text-[11px] font-black text-gray-500"
                    >
                      수정
                    </button>
                  </div>
                  {repliesByParentId[comment.id]?.length > 0 && (
                    <div className="mt-3 space-y-2 border-l-2 border-emerald-100 pl-3">
                      {repliesByParentId[comment.id].map((reply) => (
                        <div key={reply.id} className="rounded-xl bg-emerald-50/60 p-3">
                          <div className="flex justify-between items-start gap-4">
                            <div className="min-w-0">
                              <p className="truncate text-xs font-black text-gray-900">
                                {reply.authorName}
                              </p>
                              <p className="mt-1 text-[10px] font-bold text-gray-400">
                                {new Date(reply.createdAt).toLocaleString('ko-KR')}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleDeleteComment(reply.id)}
                              className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                              title="댓글 삭제"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                          <p className="mt-2 whitespace-pre-wrap break-words text-sm font-bold leading-relaxed text-gray-600">
                            {reply.content}
                          </p>
                          <button
                            type="button"
                            onClick={() => {
                              setEditingComment(reply);
                              setReplyTarget(null);
                              setAuthorName(reply.authorName);
                              setContent(reply.content);
                            }}
                            className="mt-3 text-[11px] font-black text-gray-500"
                          >
                            수정
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-6 border-t bg-gray-50/50 rounded-b-[32px]">
          <p className="text-[11px] text-gray-400 font-bold text-center">
            * 고객 댓글과 관리자 댓글을 상품별로 확인하고 관리합니다.
          </p>
        </div>
      </div>
    </div>
  );
};
